import { ComponentProps } from 'react';
import { format, isSameDay, parseISO } from 'date-fns';
import { Line } from 'react-chartjs-2';

import { GraphResponse } from '~/shared/types/api';

import { ChartTypes } from './Legends';

export const CHART_COLORS = {
  UTILIZED: '#06d727',
  SIZE: '#2151F5',
  SOURCE_SIZE: '#2151F5',
  DATAFY_DISK_USED: '#A020F0',
} as const;

export const GIB = 1024 * 1024 * 1024;
export const MIB = 1024 * 1024;
export const KIB = 1024;
export const DESIRED_TICK_COUNT = 5;
export const GIB_STEP_LOWER_BOUND = 0.5 * GIB;
const FUTURE_DATA_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Filters out data points that are more than 5 minutes in the future
 * from the last data point that has actual values (utilized, size, or datafyDiskUsed).
 * This prevents the graph from extending beyond valid data when the backend
 * sends placeholder entries with future timestamps.
 */
export function filterFutureDataPoints(data: GraphResponse['utilizationGraph']) {
  if (!data?.length) {
    return data;
  }

  let lastValidDataTimestamp: number | null = null;

  // Iterate backwards to find the last entry with actual data
  for (let i = data.length - 1; i >= 0; i--) {
    const entry = data[i];
    if (
      entry &&
      entry.ts &&
      (typeof entry.utilized === 'number' ||
        typeof entry.size === 'number' ||
        typeof entry.sourceSize === 'number' ||
        typeof entry.datafyDiskUsed === 'number')
    ) {
      lastValidDataTimestamp = parseISO(entry.ts).getTime();
      break;
    }
  }

  if (lastValidDataTimestamp === null) {
    return data;
  }

  const lastValidTimestamp = lastValidDataTimestamp;
  const cutoffTime = lastValidTimestamp + FUTURE_DATA_THRESHOLD_MS;

  // Filter out entries that are within or equal to 5 minutes after the last valid data
  return data.filter((entry) => {
    if (!entry.ts) {
      return true;
    }
    const entryTime = parseISO(entry.ts).getTime();
    // Filter out entries that are after the last valid data and within 5 minutes (including exactly 5 minutes)
    // Keep entries that are before or equal to the last valid data, or more than 5 minutes after it
    return entryTime <= lastValidTimestamp || entryTime > cutoffTime;
  });
}

export function calculateDateRange(data: GraphResponse['utilizationGraph']) {
  if (!data?.length) {
    return [null, null] as const;
  }

  const minTs = data.reduce((min, d) => {
    if (d.ts) {
      const ts = parseISO(d.ts).getTime();
      return Math.min(min, ts);
    }
    return min;
  }, Infinity);

  const maxTs = data.reduce((max, d) => {
    if (d.ts) {
      const ts = parseISO(d.ts).getTime();
      return Math.max(max, ts);
    }
    return max;
  }, -Infinity);

  if (minTs === Infinity || maxTs === -Infinity) {
    return [null, null] as const;
  }

  return [new Date(minTs), new Date(maxTs)] as const;
}

export function checkIsSingleDayData(minDate: Date | null, maxDate: Date | null) {
  if (!minDate || !maxDate) {
    return false;
  }
  return isSameDay(minDate, maxDate);
}

export function formatChartData(
  data: GraphResponse['utilizationGraph'],
  visibleDatasets: {
    utilized: boolean;
    size: boolean;
    sourceSize: boolean;
    datafyDiskUsed: boolean;
  },
  chartType: ChartTypes = 'usage'
) {
  if (!data) {
    return { datasets: [] };
  }

  const parsedData = data.map((d) => ({
    tsDate: d.ts ? parseISO(d.ts) : new Date(0),
    utilized: d.utilized,
    size: d.size,
    sourceSize: d.sourceSize ?? null,
    datafyDiskUsed: d.datafyDiskUsed,
  }));

  return {
    datasets: [
      visibleDatasets.utilized && {
        label: chartType === 'utilization' ? 'Utilization %' : 'Used',
        data: parsedData.map((d) => ({
          x: d.tsDate,
          y: (() => {
            if (d.utilized === 0 && d.size === 0) {
              return null;
            }
            if (chartType === 'utilization') {
              // Calculate utilization percentage
              if (!d.size || d.size === 0) {
                return null;
              }
              return ((d.utilized || 0) / d.size) * 100;
            }
            return d.utilized;
          })(),
        })),
        borderColor: CHART_COLORS.UTILIZED,
        backgroundColor: CHART_COLORS.UTILIZED,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 10,
      },
      visibleDatasets.sourceSize && {
        label: 'Original Size',
        data: parsedData.map((d) => ({
          x: d.tsDate,
          y: d.sourceSize ?? null,
        })),
        borderColor: CHART_COLORS.SOURCE_SIZE,
        backgroundColor: CHART_COLORS.SOURCE_SIZE,
        borderDash: [8, 4],
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        spanGaps: false,
      },
      visibleDatasets.size && {
        label: 'Size',
        data: parsedData.map((d) => ({
          x: d.tsDate,
          y: d.utilized === 0 && d.size === 0 ? null : d.size,
        })),
        borderColor: CHART_COLORS.SIZE,
        backgroundColor: CHART_COLORS.SIZE,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 10,
      },
      visibleDatasets.datafyDiskUsed && {
        label: 'Datafy Used',
        data: parsedData.map((d) => ({
          x: d.tsDate,
          y: d.utilized === 0 && d.size === 0 ? null : d.datafyDiskUsed,
        })),
        borderColor: CHART_COLORS.DATAFY_DISK_USED,
        backgroundColor: CHART_COLORS.DATAFY_DISK_USED,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 10,
      },
    ].filter(Boolean) as ComponentProps<typeof Line>['data']['datasets'],
  };
}

export function calculateYAxisScale(actualMaxVal: number, yMin = 0) {
  if (actualMaxVal === 0) {
    return { stepBytes: 1 * 1024 ** 3, yMaxForScale: 4 * 1024 ** 3 }; // 1 GiB step, 4 GiB max
  }

  // Common storage steps in bytes (GiB and TiB)
  const steps = [
    1 * 1024 ** 3, // 1 GiB
    2 * 1024 ** 3, // 2 GiB
    4 * 1024 ** 3, // 4 GiB
    8 * 1024 ** 3, // 8 GiB
    16 * 1024 ** 3, // 16 GiB
    32 * 1024 ** 3, // 32 GiB
    64 * 1024 ** 3, // 64 GiB
    128 * 1024 ** 3, // 128 GiB
    256 * 1024 ** 3, // 256 GiB
    512 * 1024 ** 3, // 512 GiB
    1 * 1024 ** 4, // 1 TiB
    2 * 1024 ** 4, // 2 TiB
    4 * 1024 ** 4, // 4 TiB
    8 * 1024 ** 4, // 8 TiB
    16 * 1024 ** 4, // 16 TiB
  ];

  // Find the smallest step that gives a max >= actualMaxVal and is divisible by 4
  for (const step of steps) {
    const max = yMin + step * 4;
    if (actualMaxVal <= max) {
      return { stepBytes: step, yMaxForScale: max };
    }
  }

  // Fallback: just round up to the next multiple of 4 of the largest step
  const step = steps[steps.length - 1];
  const max = yMin + step * Math.ceil((actualMaxVal - yMin) / step / 4) * 4;
  return { stepBytes: step, yMaxForScale: max };
}

export function calculateXAxisConfig(
  minDate: Date | null,
  maxDate: Date | null,
  isSingleDayData: boolean
) {
  const durationHours =
    minDate && maxDate ? (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60) : 0;
  const durationMinutes =
    minDate && maxDate ? (maxDate.getTime() - minDate.getTime()) / (1000 * 60) : 0;

  const is18To36HourRange = !isSingleDayData && durationHours >= 18 && durationHours <= 36;
  const isSubHourRange = durationMinutes <= 60;

  // Check for "across two days but less than 6 hours"
  const isTwoDayShortRange =
    !isSingleDayData &&
    minDate &&
    maxDate &&
    minDate.getDate() !== maxDate.getDate() &&
    durationHours < 12;

  if (isSingleDayData || isTwoDayShortRange) {
    if (minDate && maxDate) {
      if (durationMinutes <= 60) {
        return {
          xAxisUnit: 'minute' as const,
          xAxisStepSize: 10,
          is18To36HourRange,
          isSubHourRange,
          isTwoDayShortRange,
        };
      } else if (durationMinutes <= 120) {
        return {
          xAxisUnit: 'minute' as const,
          xAxisStepSize: 20,
          is18To36HourRange,
          isSubHourRange,
          isTwoDayShortRange,
        };
      } else if (durationMinutes <= 180) {
        return {
          xAxisUnit: 'minute' as const,
          xAxisStepSize: 30,
          is18To36HourRange,
          isSubHourRange,
          isTwoDayShortRange,
        };
      } else {
        return {
          xAxisUnit: 'minute' as const,
          xAxisStepSize: 60,
          is18To36HourRange,
          isSubHourRange,
          isTwoDayShortRange,
        };
      }
    }
    return {
      xAxisUnit: 'minute' as const,
      xAxisStepSize: 30,
      is18To36HourRange,
      isSubHourRange,
      isTwoDayShortRange,
    };
  } else if (is18To36HourRange) {
    return {
      xAxisUnit: 'minute' as const,
      xAxisStepSize: 5,
      // xAxisUnit: 'hour' as const,
      // xAxisStepSize: 6,
      is18To36HourRange,
      isSubHourRange,
      isTwoDayShortRange: false,
    };
  }
  return {
    xAxisUnit: 'hour' as const,
    xAxisStepSize: 12,
    is18To36HourRange,
    isSubHourRange,
    isTwoDayShortRange: false,
  };
}

export function createXAxisTickFormatter(
  isSingleDayData: boolean,
  xAxisUnit: 'minute' | 'hour',
  is18To36HourRange: boolean,
  // isSubHourRange: boolean = false,
  xAxisStepSize?: number,
  minDate?: Date | null,
  maxDate?: Date | null,
  isTwoDayShortRange?: boolean | null
) {
  let lastDisplayedDateKeyOnXAxis: string | null = null;

  return function (value: unknown) {
    const currentDate = new Date(value as number);

    // For single day or two-day short range minute data
    if ((isSingleDayData || isTwoDayShortRange) && xAxisUnit === 'minute') {
      // Only show ticks within data range
      if (minDate && maxDate) {
        if (
          currentDate.getTime() < minDate.getTime() ||
          currentDate.getTime() > maxDate.getTime()
        ) {
          return null;
        }
      }

      // Show date at 12:00 AM (start of a new day)
      if (currentDate.getHours() === 0 && currentDate.getMinutes() === 0) {
        return format(currentDate, 'MMM d');
      }

      // Always show hour boundaries
      if (currentDate.getMinutes() === 0) {
        return format(currentDate, 'h:mm a').toUpperCase();
      }

      // Show step intervals only if they're multiples of step size
      if (xAxisStepSize && currentDate.getMinutes() % xAxisStepSize === 0) {
        return format(currentDate, 'h:mm a').toUpperCase();
      }

      return null;
    }

    // Rest of the function remains the same for other cases
    if (is18To36HourRange) {
      const currentHour = currentDate.getHours();

      if (currentHour % 6 !== 0) {
        return null;
      }

      if (currentHour === 0) {
        return format(currentDate, 'MMM d');
      }

      return format(currentDate, 'h a').toUpperCase();
    } else if (!isSingleDayData) {
      const currentHour = currentDate.getHours();
      const currentDateStringKey = format(currentDate, 'yyyy-MM-dd');

      // const isLateHourTick = currentHour >= 22;

      // if (isLateHourTick && index < ticks.length - 1) {
      //   const nextTickDate = new Date(ticks[index + 1].value as number);
      //   const nextTickDay = format(nextTickDate, 'yyyy-MM-dd');

      //   if (nextTickDay !== currentDateStringKey) {
      //     return null;
      //   }
      // }

      if (currentDateStringKey !== lastDisplayedDateKeyOnXAxis) {
        lastDisplayedDateKeyOnXAxis = currentDateStringKey;
        if (currentHour === 0) {
          return format(currentDate, 'MMM d');
        } else {
          return `${format(currentDate, 'h a').toUpperCase()}`;
        }
      } else {
        return format(currentDate, 'h a').toUpperCase();
      }
    }

    return format(currentDate, 'h:mm a').toUpperCase();
  };
}

export function createYAxisTickFormatter(yMaxForScale: number, actualMaxVal: number) {
  let firstNonZeroTickUnit: string | null = null;

  return (tickValue: unknown) => {
    const numericTickValue = Number(tickValue);

    // If this is a non-zero tick and we haven't found a unit yet, capture its unit
    if (numericTickValue > 0 && firstNonZeroTickUnit === null) {
      const formattedValue = formatTickValueBytes(numericTickValue);
      const spaceIndex = formattedValue.lastIndexOf(' ');
      if (spaceIndex !== -1 && spaceIndex < formattedValue.length - 1) {
        firstNonZeroTickUnit = formattedValue.substring(spaceIndex);
      }
    }

    // Handle zero value
    if (numericTickValue === 0) {
      // If we have a unit from a non-zero tick, use it
      if (firstNonZeroTickUnit !== null) {
        return `0${firstNonZeroTickUnit}`;
      }

      // Otherwise, fall back to the original logic
      const yMaxFormatted = formatTickValueBytes(yMaxForScale);
      let unit = '';
      const spaceIndex = yMaxFormatted.lastIndexOf(' ');
      if (spaceIndex !== -1 && spaceIndex < yMaxFormatted.length - 1) {
        unit = yMaxFormatted.substring(spaceIndex);
      } else {
        const match = yMaxFormatted.match(/[a-zA-Z]+$/);
        if (match) {
          unit = ` ${match[0]}`;
        } else if (yMaxForScale > 0 && (yMaxForScale < KIB || actualMaxVal === 0)) {
          unit = ' Bytes';
        }
      }
      return `0${unit}`;
    }

    return formatTickValueBytes(numericTickValue);
  };
}

export function calculateNiceTickStepInBytes(
  maxValue: number,
  desiredTickCount: number,
  unit: number
): number {
  if (maxValue <= 0 || desiredTickCount <= 0 || unit <= 0) {
    return unit;
  }

  const valueInUnits = maxValue / unit;
  const rawStep = valueInUnits / desiredTickCount;
  if (rawStep <= 0) {
    // Prevent Math.log10(non-positive)
    return unit;
  }
  const magnitude = Math.floor(Math.log10(rawStep));
  const niceBase = Math.ceil(rawStep / Math.pow(10, magnitude));

  return Math.max(1, niceBase * Math.pow(10, magnitude)) * unit;
}

export function formatTickValueBytes(bytesValue: number): string {
  const k = 1024;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  if (bytesValue === 0) {
    return '0 Bytes';
  }

  const absoluteBytes = Math.abs(bytesValue);
  let i = 0;
  if (absoluteBytes >= 1) {
    i = Math.floor(Math.log(absoluteBytes) / Math.log(k));
  }

  // For values in GiB (index 3), use whole numbers with no decimal places
  const decimals = i === 3 ? 0 : 1; // Use 0 decimals for GiB, 1 for others

  const valueInUnit = bytesValue / Math.pow(k, i);

  // Format with fixed decimals first
  const formattedValue = valueInUnit.toFixed(decimals);

  // Remove trailing zeros and decimal point if needed
  const cleanedValue = formattedValue.replace(/\.0+$/, '');

  return `${cleanedValue} ${sizes[i]}`;
}

export function isWithin24Hours(timestamps: string[]): boolean {
  if (!timestamps || timestamps.length === 0) {
    return true;
  }

  const dates = timestamps
    .map((timestamp) => (timestamp ? parseISO(timestamp).getTime() : NaN))
    .filter((time) => Number.isFinite(time));

  if (dates.length === 0) {
    return true;
  }
  if (dates.length === 1) {
    return true;
  }

  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);

  const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000;
  return maxDate - minDate <= twentyFourHoursInMilliseconds;
}
